import type { CSSProperties, ReactNode } from 'react';

/**
 * Shared status-banner primitives for the consultant UI.
 *
 * A11y review 2026-06-30 (HIGH): error/success banners were rendered as bare
 * `<div className="error-banner">` / `<div className="success-banner">` with no
 * ARIA semantics, so assistive technology never announced them. These two
 * components bake in the correct live-region roles so every call site announces
 * automatically:
 *
 *   - <ErrorBanner>   → role="alert"  aria-live="assertive"  (interrupts — a
 *                       failed mutation needs immediate attention)
 *   - <SuccessBanner> → role="status" aria-live="polite"     (waits for a pause
 *                       — a save confirmation is not urgent)
 *
 * They are deliberately thin: same `error-banner` / `success-banner` CSS class,
 * and they pass `className` / `style` straight through, so swapping the existing
 * inline `<div>`s for these is purely a semantics change with ZERO visual diff.
 *
 * Note: `aria-live` regions only announce mutations to an *already-present*
 * node. Because these banners are conditionally mounted (`{err && <ErrorBanner/>}`),
 * the role + aria-live pair is what guarantees announcement on first render —
 * `role="alert"` implies `aria-live="assertive"`, but we set both explicitly so
 * the intent is unambiguous and robust across screen readers.
 */

interface BannerProps {
  children: ReactNode;
  /** Extra classes appended after the base banner class. */
  className?: string;
  /** Inline style passthrough — preserves per-call-site spacing overrides. */
  style?: CSSProperties;
  /** Optional test/debug hook id, mirrors existing data-debug-id usage. */
  'data-debug-id'?: string;
}

export function ErrorBanner({ children, className, style, ...rest }: BannerProps) {
  return (
    <div
      className={className ? `error-banner ${className}` : 'error-banner'}
      style={style}
      role="alert"
      aria-live="assertive"
      {...rest}
    >
      {children}
    </div>
  );
}

export function SuccessBanner({ children, className, style, ...rest }: BannerProps) {
  return (
    <div
      className={className ? `success-banner ${className}` : 'success-banner'}
      style={style}
      role="status"
      aria-live="polite"
      {...rest}
    >
      {children}
    </div>
  );
}
